import React, { useState, useEffect } from "react";
import _ from "lodash";
import qoute from "../../Assets/quote.svg";
import { API_URL } from "../../common/constant";
import "./TestimonialComponent.css";

const TestimonialComponent = ({ 
  autoRotate = true, 
  rotationInterval = 5000, 
  showBullets = true,
  className = "",
  onTestimonialChange = null 
}) => {
  const [testimonials, setTestimonials] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [autoRotateInterval, setAutoRotateInterval] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [slideDirection, setSlideDirection] = useState('right');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [bulletScrollPosition, setBulletScrollPosition] = useState(0);

  const MAX_VISIBLE_BULLETS = 5;

  const getWebsiteStatistics = () => {
    setIsLoading(true);
    fetch(`${API_URL}api/getWebsiteStatistics`)
      .then((response) => response.json())
      .then((response) => {
        if (response.data && response.data.testimonials && response.data.testimonials.length > 0) {
          setTestimonials(response.data.testimonials);
          setActiveTestimonial(response.data.testimonials[0]);
        } else {
          const fallbackTestimonial = {
            _id: "fallback",
            message: "The technical interviews conducted by Rala Infotech were thorough and insightful. Their panel's expertise in Java and microservices was particularly valuable, and their feedback helped us make informed hiring decisions. The free mock interviews were a great way to experience their quality firsthand",
            author: "Sreenath Prabhu",
            designation: "Accion Labs India"
          };
          setTestimonials([fallbackTestimonial]);
          setActiveTestimonial(fallbackTestimonial);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching testimonials:", error);
        const fallbackTestimonial = {
          _id: "fallback",
          message: "The technical interviews conducted by Rala Infotech were thorough and insightful. Their panel's expertise in Java and microservices was particularly valuable, and their feedback helped us make informed hiring decisions. The free mock interviews were a great way to experience their quality firsthand",
          author: "Sreenath Prabhu",
          designation: "Accion Labs India"
        };
        setTestimonials([fallbackTestimonial]);
        setActiveTestimonial(fallbackTestimonial);
        setIsLoading(false);
      });
  };

  const nextTestimonial = (index) => {
    if (testimonials.length === 0) return;
    
    const nextIndex = index < testimonials.length ? index : 0;
    const newActiveTestimonial = testimonials[nextIndex];
    
    const currentIndex = testimonials.findIndex(t => t._id === activeTestimonial?._id);
    const direction = nextIndex > currentIndex || (currentIndex === testimonials.length - 1 && nextIndex === 0) ? 'left' : 'right';
    
    setSlideDirection(direction);
    setIsTransitioning(true);
    
    setTimeout(() => {
      setActiveTestimonial(newActiveTestimonial);
      setIsTransitioning(false);
      updateBulletScrollPosition(nextIndex);
    }, 300);
    
    if (onTestimonialChange) {
      onTestimonialChange(newActiveTestimonial, nextIndex);
    }
  };

  const goToTestimonial = (index) => {
    if (index >= 0 && index < testimonials.length && !isTransitioning) {
      const newActiveTestimonial = testimonials[index];
      const currentIndex = testimonials.findIndex(t => t._id === activeTestimonial?._id);
      
      const direction = index > currentIndex ? 'left' : 'right';
      setSlideDirection(direction);
      setIsTransitioning(true);
      
      setTimeout(() => {
        setActiveTestimonial(newActiveTestimonial);
        setIsTransitioning(false);
        updateBulletScrollPosition(index);
      }, 300);
      
      if (onTestimonialChange) {
        onTestimonialChange(newActiveTestimonial, index);
      }
    }
  };

  const updateBulletScrollPosition = (activeIndex) => {
    if (testimonials.length <= MAX_VISIBLE_BULLETS) {
      setBulletScrollPosition(0);
      return;
    }

    // Calculate the optimal scroll position to keep active bullet visible
    const maxScroll = testimonials.length - MAX_VISIBLE_BULLETS;
    let newScrollPosition = Math.max(0, activeIndex - Math.floor(MAX_VISIBLE_BULLETS / 2));
    newScrollPosition = Math.min(newScrollPosition, maxScroll);
    
    setBulletScrollPosition(newScrollPosition);
  };

  const getVisibleBullets = () => {
    if (testimonials.length <= MAX_VISIBLE_BULLETS) {
      return testimonials;
    }
    
    return testimonials.slice(bulletScrollPosition, bulletScrollPosition + MAX_VISIBLE_BULLETS);
  };

  const getBulletIndex = (testimonial) => {
    return testimonials.findIndex(t => t._id === testimonial._id);
  };

  // Auto-rotation effect
  useEffect(() => {
    if (autoRotate && testimonials.length > 1 && !isTransitioning) {
      const interval = setInterval(() => {
        if (!isHovering) {
          const currentIndex = testimonials.findIndex(
            t => t._id === activeTestimonial?._id
          );
          const nextIndex = (currentIndex + 1) % testimonials.length;
          nextTestimonial(nextIndex);
        }
      }, rotationInterval);
      
      setAutoRotateInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [testimonials, activeTestimonial, isHovering, autoRotate, rotationInterval, isTransitioning]);

  // Initial data fetch
  useEffect(() => {
    getWebsiteStatistics();
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (autoRotateInterval) {
        clearInterval(autoRotateInterval);
      }
    };
  }, [autoRotateInterval]);

  if (isLoading) {
    return (
      <div className={`testimonials-container ${className}`}>
        <div className="row testimonials">
          <div className="col-12 col-sm-12 col-md-12 col-lg-16 text-start pt-5">
            <div className="testimonial-skeleton">
              <div className="skeleton-quote"></div>
              <div className="skeleton-text"></div>
              <div className="skeleton-author"></div>
              <div className="skeleton-designation"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!activeTestimonial) {
    return null;
  }

  const visibleBullets = getVisibleBullets();
  const showScrollArrows = testimonials.length > MAX_VISIBLE_BULLETS;

  return (
    <div 
      className={`testimonials-container ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="row testimonials">
        <div className="col-12 col-sm-12 col-md-12 col-lg-16 text-start pt-5">
          <div className={`testimonial-slide ${slideDirection} ${isTransitioning ? 'transitioning' : ''}`}>
            <img src={qoute} className="mb-4 testimonial-quote" alt="Quote" />
            <p className="text_highlite testimonial-message">
              {activeTestimonial.message}
            </p>
            <div className="text-white text-uppercase fw-bold testimonial-author">
              {activeTestimonial.author}
            </div>
            <div className="text_highlite_t testimonial-designation">
              {activeTestimonial.designation}
            </div>
          </div>
        </div>
        
        {testimonials.length > 1 && (
          <div className="carousel-bullets-container">
            {showScrollArrows && bulletScrollPosition > 0 && (
              <button 
                className="bullet-scroll-arrow bullet-scroll-left"
                onClick={() => setBulletScrollPosition(Math.max(0, bulletScrollPosition - 1))}
                disabled={isTransitioning}
              >
                ‹
              </button>
            )}
            
            <div className="carousel-bullets mt-3">
              {visibleBullets.map((testimonial) => {
                const actualIndex = getBulletIndex(testimonial);
                const isActive = testimonial._id === activeTestimonial._id;
                
                return (
                  <div 
                    key={testimonial._id}
                    onClick={() => goToTestimonial(actualIndex)} 
                    className={`bullet ${isActive ? 'active' : ''} ${isTransitioning ? 'disabled' : ''}`}
                    title={`Testimonial ${actualIndex + 1}`}
                  />
                );
              })}
            </div>
            
            {showScrollArrows && bulletScrollPosition < testimonials.length - MAX_VISIBLE_BULLETS && (
              <button 
                className="bullet-scroll-arrow bullet-scroll-right"
                onClick={() => setBulletScrollPosition(Math.min(testimonials.length - MAX_VISIBLE_BULLETS, bulletScrollPosition + 1))}
                disabled={isTransitioning}
              >
                ›
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialComponent; 